import { GcLoader } from '@garmin/components-global';
import { mapGetters, mapActions } from 'vuex';
import SpinSVG from '@/assets/icons/drag_to_spin.svg';
import loadImages from '../../helpers/preloadImages';

export default {
  name: 'ProductSpinner',
  props: {
    images: {
      type: Array,
      required: true,
    },
    infinite: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    speed: {
      type: Number,
      required: false,
      default: () => 3,
    },
    mouseWheel: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    slider: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    sliderClass: {
      type: String,
      required: false,
      default: () => '',
    },
  },
  components: {
    GcLoader,
    SpinSVG,
  },
  data() {
    return {
      speedController: 0,
      spinner: {
        current: 0,
        size: 0,
        currentPath: null,
      },
      mouse: {
        isMoving: false,
        isDragging: false,
      },
      touch: {
        isMoving: false,
        initialX: 0,
        isDragging: false,
      },
      lastPosition: 0,
    };
  },
  computed: {
    imagesPreloaded() {
      return this.loadedSpinnerImages[this.partNumber];
    },
    ...mapGetters({
      partNumber: 'getPartNumber',
      loadedSpinnerImages: 'getLoadedSpinnerImages',
      productSpinnerImages: 'getProductSpinnerImagesBySku',
      startedPreloading: 'getStartedPreloadingBySku',
    }),
  },
  watch: {
    imagesPreloaded() {
      this.initializeSpinner();
    },
  },
  beforeMount() {
    // If users go to the spinner route for a non-supported product route back
    if (!this.productSpinnerImages(this.partNumber)) {
      this.$router.push({
        name: 'FullPath',
        params: this.$router.currentRoute.params,
        hash: this.$router.currentRoute.hash,
      });
      return;
    }

    if (this.imagesPreloaded) {
      this.initializeSpinner();
    }
  },
  mounted() {
    // Start loading the images
    this.preloadImages();

    // Hide the header and footer when DOM is ready
    this.onDocumentReady(this.toggleHeader);

    // Add event listeners
    window.addEventListener(
      'mouseup',
      this.handleMouseDragging,
    );
    window.addEventListener(
      'touchend',
      this.handleTouchDragging,
    );
    if (this.mouseWheel) {
      this.$refs.componentContainer.addEventListener(
        'wheel',
        this.handleWheel,
        false,
      );
    }
  },
  beforeDestroy() {
    // Toggle the header and footer back
    this.toggleHeader();

    // Remove event listeners
    if (this.mouseWheel) {
      this.$refs.componentContainer.removeEventListener(
        'wheel',
        this.handleWheel,
      );
    }
    window.removeEventListener(
      'mouseup',
      this.handleMouseDragging,
    );
    window.removeEventListener(
      'touchend',
      this.handleTouchDragging,
    );
  },
  methods: {
    ...mapActions([
      'setStartedPreloading',
      'setLoadedSpinnerImages',
    ]),
    onDocumentReady(fn) {
      // see if DOM is already available
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        // call on next available tick
        setTimeout(fn, 1);
      } else {
        document.addEventListener('DOMContentLoaded', fn);
      }
    },
    toggleHeader() {
      const header = document.querySelector('.gh__header');
      const footer = document.querySelector('.gf__footer');

      if (header) {
        header.style.display = header.style.display === 'none' ? 'block' : 'none';
      }

      if (footer) {
        footer.style.display = footer.style.display === 'none' ? 'block' : 'none';
      }
    },
    preloadImages() {
      // The preloadImages function is called either when the user hovers on gallery
      // or enters directly on the viewer, with this parameter we can make sure
      // is not called twice
      const sku = this.partNumber;

      if (!this.startedPreloading(sku)) {
        this.setStartedPreloading({ sku, value: true });
        const images = this.productSpinnerImages(sku) || [];
        const loadedImages = this.loadedSpinnerImages[sku];
        const promises = [];

        if (loadedImages || !images.length) {
          return;
        }

        // Preload the images one by one
        images.forEach((src) => {
          promises.push(loadImages(src, null, this.removeBrokenImage));
        });

        // After all are done set them as loaded in the store
        Promise.all(promises).then(() => {
          this.setLoadedSpinnerImages({
            sku,
            value: true,
          });
        });
      }
    },
    initializeSpinner() {
      this.spinner.size = this.images.length;
      [this.spinner.currentPath] = this.images;
    },
    handleKeydown(event) {
      if (event.keyCode === 39) {
        event.preventDefault();
        this.handleMovement(this.lastPosition + 1);
      }
      if (event.keyCode === 37) {
        event.preventDefault();
        this.handleMovement(this.lastPosition - 1);
      }
    },
    handleWheel(event) {
      if (this.mouseWheel) {
        event.preventDefault();
        this.handleMovement(event.deltaY);
      }
    },
    handleSlider(event) {
      this.spinner.current = Number(event.target.value);
      this.spinner.currentPath = this.images[event.target.value - 1];
    },
    handleMouseDown() {
      this.mouse.isDragging = true;
      this.mouse.isMoving = true;
    },
    handleMouseUp() {
      this.mouse.isMoving = false;
    },
    handleMouseMove(event) {
      if (this.mouse.isMoving && this.mouse.isDragging) {
        this.handleMovement(event.pageX);
      }
    },
    handleMouseDragging() {
      this.mouse.isDragging = false;
    },
    handleTouchDragging() {
      this.touch.isDragging = false;
    },
    handleTouchStart(event) {
      event.preventDefault();
      this.touch.isMoving = true;
      this.touch.isDragging = true;
      this.touch.initialX = event.touches[0].pageX;
    },
    handleTouchEnd() {
      this.touch.isMoving = false;
    },
    handleTouchMove(event) {
      if (this.touch.isDragging) {
        const delta = -(this.touch.initialX - event.touches[0].pageX);
        this.handleMovement(delta);
      }
    },
    handleMovement(delta) {
      this.speedController += 1;
      // if the speed param is by default 3, the movement will be handled
      // only once at 3 function calls
      // by increasing the speed param, it will slow down the movement
      // by decreasing the speed param, it will speed up the movement
      if (this.speedController < this.speed) {
        return;
      }
      if (this.speedController > this.speed) {
        this.speedController = 0;
      }
      // if the current position (delta) is bigger than the lastPosition it goes to the right
      if (delta > this.lastPosition) {
        // if it's not the last image from the array, go to the next image to the right
        if (this.spinner.current >= 0 && this.spinner.current < this.spinner.size) {
          this.spinner.current += 1;
          this.spinner.currentPath = this.images[this.spinner.current - 1];
        // if infinite is true, it starts again with the first image
        } else if (this.infinite) {
          this.spinner.current = 1;
          this.spinner.currentPath = this.images[this.spinner.current - 1];
        }
      // if the current position (delta) is smaller than the lastPosition it goes to the left
      } else if (delta < this.lastPosition) {
        // if the current image is not the first one, go to the next image to the left
        if (this.spinner.current >= 0 && this.spinner.current - 1 > 0) {
          this.spinner.current -= 1;
          this.spinner.currentPath = this.images[this.spinner.current - 1];
        // if infinite is true, it starts again with the last image
        } else if (this.infinite) {
          this.spinner.current = this.spinner.size;
          this.spinner.currentPath = this.images[this.spinner.current - 1];
        }
      }
      this.lastPosition = delta;
    },
  },
};
